/**
 * "What the assistant can and can't do" — Core concepts section.
 *
 * The single plain-language capability + limits sheet a non-technical buyer or
 * admin can read to understand the assistant's blast radius: what it acts as,
 * what it can and cannot do (a real negative inventory), how risky actions are
 * gated, what a conversation looks like step by step, what happens if it is
 * tricked or gets something wrong, and why every action is attributable.
 *
 * This page is a SYNTHESIS of guarantees stated elsewhere (browser-gate,
 * identity, confirmation, the widget boundary) so a reader gets the whole story
 * in one place — it must not claim anything those pages don't. The published
 * posture on prompt-injection is "mitigated, not eliminated"; the "tricked"
 * section reflects that honestly and never claims immunity.
 */
import type { DocPageContent } from '../types';

export const BOUNDARIES_PAGE: DocPageContent = {
  slug: 'boundaries',
  titleKey: 'docs.concepts.boundaries.title',
  descriptionKey: 'docs.concepts.boundaries.description',
  blocks: [
    { kind: 'paragraph', textKey: 'docs.concepts.boundaries.lead' },
    {
      kind: 'callout',
      tone: 'important',
      titleKey: 'docs.concepts.boundaries.ceiling.title',
      textKey: 'docs.concepts.boundaries.ceiling.body',
    },

    { kind: 'heading', level: 2, id: 'can', textKey: 'docs.concepts.boundaries.can.heading' },
    { kind: 'paragraph', textKey: 'docs.concepts.boundaries.can.body' },
    {
      kind: 'list',
      itemKeys: [
        'docs.concepts.boundaries.can.item.answer',
        'docs.concepts.boundaries.can.item.read',
        'docs.concepts.boundaries.can.item.act',
        'docs.concepts.boundaries.can.item.render',
        'docs.concepts.boundaries.can.item.remember',
      ],
    },

    { kind: 'heading', level: 2, id: 'cant', textKey: 'docs.concepts.boundaries.cant.heading' },
    { kind: 'paragraph', textKey: 'docs.concepts.boundaries.cant.body' },
    {
      kind: 'list',
      itemKeys: [
        'docs.concepts.boundaries.cant.item.exceed',
        'docs.concepts.boundaries.cant.item.impersonate',
        'docs.concepts.boundaries.cant.item.destructive',
        'docs.concepts.boundaries.cant.item.skip',
        'docs.concepts.boundaries.cant.item.secrets',
        'docs.concepts.boundaries.cant.item.page',
        'docs.concepts.boundaries.cant.item.credentials',
      ],
    },

    { kind: 'heading', level: 2, id: 'gating', textKey: 'docs.concepts.boundaries.gating.heading' },
    { kind: 'paragraph', textKey: 'docs.concepts.boundaries.gating.body' },
    {
      kind: 'definitions',
      items: [
        {
          termKey: 'docs.concepts.boundaries.tier.read.term',
          descKey: 'docs.concepts.boundaries.tier.read.desc',
        },
        {
          termKey: 'docs.concepts.boundaries.tier.write.term',
          descKey: 'docs.concepts.boundaries.tier.write.desc',
        },
        {
          termKey: 'docs.concepts.boundaries.tier.destructive.term',
          descKey: 'docs.concepts.boundaries.tier.destructive.desc',
        },
      ],
    },
    {
      kind: 'callout',
      tone: 'note',
      titleKey: 'docs.concepts.boundaries.gating.undo.title',
      textKey: 'docs.concepts.boundaries.gating.undo.body',
    },

    {
      kind: 'heading',
      level: 2,
      id: 'walkthrough',
      textKey: 'docs.concepts.boundaries.walkthrough.heading',
    },
    { kind: 'paragraph', textKey: 'docs.concepts.boundaries.walkthrough.body' },
    {
      kind: 'steps',
      steps: [
        {
          titleKey: 'docs.concepts.boundaries.walkthrough.step.ask.title',
          textKey: 'docs.concepts.boundaries.walkthrough.step.ask.text',
        },
        {
          titleKey: 'docs.concepts.boundaries.walkthrough.step.identity.title',
          textKey: 'docs.concepts.boundaries.walkthrough.step.identity.text',
        },
        {
          titleKey: 'docs.concepts.boundaries.walkthrough.step.plan.title',
          textKey: 'docs.concepts.boundaries.walkthrough.step.plan.text',
        },
        {
          titleKey: 'docs.concepts.boundaries.walkthrough.step.confirm.title',
          textKey: 'docs.concepts.boundaries.walkthrough.step.confirm.text',
        },
        {
          titleKey: 'docs.concepts.boundaries.walkthrough.step.run.title',
          textKey: 'docs.concepts.boundaries.walkthrough.step.run.text',
        },
        {
          titleKey: 'docs.concepts.boundaries.walkthrough.step.record.title',
          textKey: 'docs.concepts.boundaries.walkthrough.step.record.text',
        },
      ],
    },

    {
      kind: 'heading',
      level: 2,
      id: 'tricked',
      textKey: 'docs.concepts.boundaries.tricked.heading',
    },
    { kind: 'paragraph', textKey: 'docs.concepts.boundaries.tricked.body' },
    {
      kind: 'list',
      itemKeys: [
        'docs.concepts.boundaries.tricked.item.ceiling',
        'docs.concepts.boundaries.tricked.item.provenance',
        'docs.concepts.boundaries.tricked.item.gated',
        'docs.concepts.boundaries.tricked.item.undo',
      ],
    },
    {
      kind: 'callout',
      tone: 'note',
      titleKey: 'docs.concepts.boundaries.tricked.honest.title',
      textKey: 'docs.concepts.boundaries.tricked.honest.body',
    },

    { kind: 'heading', level: 2, id: 'next', textKey: 'docs.common.nextSteps' },
    {
      kind: 'cardGrid',
      cards: [
        {
          titleKey: 'docs.concepts.boundaries.next.browserGate.title',
          textKey: 'docs.concepts.boundaries.next.browserGate.text',
          to: { docSlug: 'browser-gate' },
        },
        {
          titleKey: 'docs.concepts.boundaries.next.confirmation.title',
          textKey: 'docs.concepts.boundaries.next.confirmation.text',
          to: { docSlug: 'confirmation' },
        },
        {
          titleKey: 'docs.concepts.boundaries.next.security.title',
          textKey: 'docs.concepts.boundaries.next.security.text',
          to: { docSlug: 'trust/security' },
        },
      ],
    },
  ],
};
